import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Invoice,
  InvoiceDownload,
  InvoicePaperSize,
  IssueInvoiceInput,
  OrderInvoices,
  SalesRegister,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { orderKeys } from '../orders/use-orders.js';
import {
  creditInvoice,
  getInvoiceDownload,
  getOrderInvoices,
  getSalesRegister,
  issueInvoice,
  sendInvoice,
  type SalesRegisterParams,
} from './invoices-api.js';

/**
 * The invoicing hooks.
 *
 * None of these goes through the offline write queue, which is the one judgement call here and
 * is the opposite of the call task 17 made for payments. Issuing an invoice consumes a number in
 * a legally sequential series and gives a customer a document; replaying that write twenty
 * minutes later, from a queue, against a shop's settings that may have changed in between, is
 * not a thing anybody wants to happen quietly. A bill that fails to issue because the connection
 * dropped is a bill the shop issues again, deliberately, when it is back.
 */

export const invoiceKeys = {
  all: ['invoices'] as const,
  order: (orderId: string) => ['invoices', 'order', orderId] as const,
  register: (params: SalesRegisterParams) => ['invoices', 'sales-register', params] as const,
};

export function useOrderInvoices(orderId: string | null): UseQueryResult<OrderInvoices> {
  const client = useApiClient();
  return useQuery({
    queryKey: invoiceKeys.order(orderId ?? ''),
    queryFn: () => getOrderInvoices(client, orderId as string),
    enabled: orderId !== null,
  });
}

export function useIssueInvoice(
  orderId: string,
): UseMutationResult<Invoice, unknown, IssueInvoiceInput | void> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: IssueInvoiceInput | void) =>
      issueInvoice(client, orderId, input ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

/**
 * The same issue, for an order that does not exist yet when the hook is created — the booking
 * screen, which learns the order's id only from the booking's own response.
 */
export function useIssueInvoiceForOrder(): UseMutationResult<
  Invoice,
  unknown,
  { orderId: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId }: { orderId: string }) => issueInvoice(client, orderId, undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

export function useCreditInvoice(): UseMutationResult<
  Invoice,
  unknown,
  { invoiceId: string; reason: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: string; reason: string }) =>
      creditInvoice(client, invoiceId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

export function useSendInvoice(): UseMutationResult<
  Invoice,
  unknown,
  { invoiceId: string; paperSize?: InvoicePaperSize }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, paperSize }: { invoiceId: string; paperSize?: InvoicePaperSize }) =>
      sendInvoice(client, invoiceId, paperSize ?? 'a4'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/**
 * Ask for a link and open it.
 *
 * Deliberately a mutation rather than a query: the link is short-lived and re-rendering the PDF
 * has a cost, so it is fetched when somebody presses a button, never on render.
 */
export function useInvoiceDownload(): UseMutationResult<
  InvoiceDownload,
  unknown,
  { invoiceId: string; paperSize: InvoicePaperSize; refresh?: boolean }
> {
  const client = useApiClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      paperSize,
      refresh,
    }: {
      invoiceId: string;
      paperSize: InvoicePaperSize;
      refresh?: boolean;
    }) => getInvoiceDownload(client, invoiceId, paperSize, { refresh: refresh ?? true }),
  });
}

export function useSalesRegister(params: SalesRegisterParams): UseQueryResult<SalesRegister> {
  const client = useApiClient();
  return useQuery({
    queryKey: invoiceKeys.register(params),
    queryFn: () => getSalesRegister(client, params),
    placeholderData: (previous) => previous,
  });
}
